import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   Paper,
   Tabs,
   Tab,
   Grid,
   Card,
   CardMedia,
   CardContent,
   CardActionArea,
   Checkbox,
   FormControlLabel,
   CircularProgress,
   TextField,
   InputAdornment,
   IconButton,
   Chip,
   Tooltip,
} from '@mui/material';
import {
   Event as EventIcon,
   CardTravel as TripIcon,
   Person as ProfileIcon,
   Search as SearchIcon,
   Clear as ClearIcon,
   CheckCircle as SelectedIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import { SelectedPhoto } from '../types';
import { db } from '../../../../firebase/config';
import { collection, query, where, getDocs, orderBy, limit, doc } from 'firebase/firestore';
import { get_user_trips_photos } from '../../../../services';
import { get_user_events_photos } from '../../../../services';
import { get_user_photos } from '../../../../services';
// Tab panel component for photo sources
interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

function TabPanel(props: TabPanelProps) {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`photo-source-tabpanel-${index}`}
         aria-labelledby={`photo-source-tab-${index}`}
         {...other}
      >
         {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
      </div>
   );
}

interface PhotoSelectorProps {
   onPhotoSelect: (photos: SelectedPhoto[]) => void;
   selectedPhotos: SelectedPhoto[];
}

const PhotoSelector: React.FC<PhotoSelectorProps> = ({ onPhotoSelect, selectedPhotos }) => {
   // State management
   const [tabIndex, setTabIndex] = useState(0);
   const [loading, setLoading] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');
   const [eventPhotos, setEventPhotos] = useState<any[]>([]);
   const [tripPhotos, setTripPhotos] = useState<any[]>([]);
   const [profilePhotos, setProfilePhotos] = useState<any[]>([]);
   const [selectedSourceTypes, setSelectedSourceTypes] = useState<
      ('event' | 'trip' | 'user_photo')[]
   >(['event', 'trip', 'user_photo']);
   const { currentUser } = useAuth();

   const loadPhotos = async () => {
      setLoading(true);
      try {
         console.log('loading photos');
         const eventPhotos = await get_user_events_photos(
            currentUser?.uid || '',
            currentUser?.email || ''
         );

         console.log(eventPhotos, 'event phoot');
         const tripPhotos = await get_user_trips_photos(
            currentUser?.uid || '',
            currentUser?.email || ''
         );

         console.log(tripPhotos, 'trip phoot');

         const profilePhotos = await get_user_photos(currentUser?.uid || '');
         setEventPhotos(eventPhotos);
         setTripPhotos(tripPhotos);
         setProfilePhotos(profilePhotos);
      } catch (error) {
         console.error('Error loading photos:', error);
      } finally {
         setLoading(false);
      }
   };

   // Load photos from different sources
   useEffect(() => {
      loadPhotos();
   }, []);

   useEffect(() => {
      console.log('PhotoSelector grid eventPhotos:', eventPhotos);
      console.log('PhotoSelector grid tripPhotos:', tripPhotos);
      console.log('PhotoSelector grid profilePhotos:', profilePhotos);
   }, [eventPhotos, tripPhotos, profilePhotos]);

   const handlePhotoSelect = (photo: any) => {
      // Check if the photo is already selected
      const isSelected = selectedPhotos.some((selected) => selected.id === photo.id);

      // Check if this user can transform this photo
      if (!photo.canTransform && !isSelected) {
         // Only show warning if attempting to select a non-transformable photo
         alert("You can only transform photos that you've uploaded yourself.");
         return;
      }

      if (isSelected) {
         // Remove the photo if it's already selected
         onPhotoSelect(selectedPhotos.filter((selected) => selected.id !== photo.id));
      } else {
         // Add the photo to the selection
         onPhotoSelect([
            {
               id: photo.id,
               url: photo.url,
               thumbnailUrl: photo.thumbnailUrl || photo.url,
               title: photo.title || 'Photo',
               type: photo.type,
               tripId: photo.tripId,
               eventId: photo.eventId,
               userId: photo.userId,
            },
         ]);
      }
   };

   // Check if a photo is selected
   const isPhotoSelected = (photoId: string) => {
      return selectedPhotos.some((p) => p.id === photoId);
   };

   // Filter photos based on search term
   const filterPhotos = (photos: any[]) => {
      if (!searchTerm.trim()) return photos;

      return photos.filter(
         (photo) =>
            photo.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            photo.eventName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            photo.tripName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
   };

   // Render enhanced photo grid with metadata
   const renderEnhancedPhotoGrid = (photos: any[]) => {
      const filteredPhotos = filterPhotos(photos);

      if (filteredPhotos.length === 0) {
         return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
               <Typography variant='body1' color='text.secondary'>
                  No photos found matching your criteria
               </Typography>
            </Box>
         );
      }

      return (
         <>
            {filteredPhotos.map((photo, index) => {
               // Determine source type for the photo
               let sourceType: 'event' | 'trip' | 'user_photo' = photo?.type;

               // Get date formatted
               const photoDate = photo.createdAt
                  ? new Date(photo.createdAt).toLocaleDateString()
                  : '';

               return (
                  <Grid item xs={3} sm={3} md={3} key={`${photo.id}-${index}`}>
                     <Card
                        sx={{
                           position: 'relative',
                           height: '150px',
                           width: '100%',
                           display: 'flex',
                           flexDirection: 'column',
                           border: isPhotoSelected(photo.id)
                              ? '2px solid #1976d2'
                              : sourceType === 'trip'
                                ? '1px solid rgba(156, 39, 176, 0.3)'
                                : sourceType === 'user_photo'
                                  ? '1px solid rgba(46, 125, 50, 0.3)'
                                  : sourceType === 'event'
                                    ? '1px solid rgba(25, 118, 210, 0.3)'
                                    : 'none',
                           borderRadius: '8px',
                           transition: 'all 0.2s ease',
                           cursor: 'pointer',
                           '&:hover': {
                              boxShadow: 6,
                              borderColor: 'success.main',
                           },
                        }}
                     >
                        <CardActionArea
                           onClick={() => handlePhotoSelect(photo)}
                           disabled={
                              !photo.canTransform &&
                              !selectedPhotos.some((selected) => selected.id === photo.id)
                           }
                           sx={{
                              flexGrow: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              opacity: photo.canTransform ? 1 : 0.7,
                              '&:hover': {
                                 transform: photo.canTransform ? 'scale(1.03)' : 'none',
                              },
                           }}
                        >
                           <Box sx={{ position: 'relative' }}>
                              <CardMedia
                                 component='img'
                                 height={sourceType === 'user_photo' ? '260' : '240'}
                                 image={photo.thumbnailUrl || photo.url}
                                 alt={photo.title}
                                 sx={{
                                    objectFit: 'cover',
                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                    borderRadius:
                                       sourceType === 'user_photo' ? '8px 8px 0 0' : '4px 4px 0 0',
                                    filter: sourceType === 'trip' ? 'saturate(1.1)' : 'none',
                                    '&::before': {
                                       content: '""',
                                       position: 'absolute',
                                       top: 0,
                                       left: 0,
                                       width: '100%',
                                       height: '100%',
                                       backgroundColor: 'rgba(0,0,0,0.03)',
                                    },
                                 }}
                              />

                              {/* Source type badge */}
                              <Chip
                                 icon={
                                    sourceType === 'event' ? (
                                       <EventIcon />
                                    ) : sourceType === 'trip' ? (
                                       <TripIcon />
                                    ) : (
                                       <ProfileIcon />
                                    )
                                 }
                                 label={sourceType.charAt(0).toUpperCase() + sourceType.slice(1)}
                                 size='small'
                                 color={
                                    sourceType === 'event'
                                       ? 'primary'
                                       : sourceType === 'trip'
                                         ? 'secondary'
                                         : 'success'
                                 }
                                 sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    backgroundColor:
                                       sourceType === 'event'
                                          ? 'rgba(25, 118, 210, 0.95)'
                                          : sourceType === 'trip'
                                            ? 'rgba(156, 39, 176, 0.95)'
                                            : 'rgba(46, 125, 50, 0.95)',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                 }}
                              />

                              {/* Selection indicator */}
                              {isPhotoSelected(photo.id) && (
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: 8,
                                       right: 8,
                                       bgcolor: 'background.paper',
                                       borderRadius: '50%',
                                       boxShadow: 2,
                                    }}
                                 >
                                    <SelectedIcon color='primary' />
                                 </Box>
                              )}
                           </Box>

                           <CardContent sx={{ p: 1.5, flexGrow: 1 }}></CardContent>
                        </CardActionArea>
                     </Card>
                  </Grid>
               );
            })}
         </>
      );
   };

   const toggleSourceType = (sourceType: 'event' | 'trip' | 'user_photo') => {
      if (selectedSourceTypes.includes(sourceType)) {
         // Don't remove if it's the last one
         //  if (selectedSourceTypes.length > 1) {
         setSelectedSourceTypes(selectedSourceTypes.filter((type) => type !== sourceType));
         // }
      } else {
         setSelectedSourceTypes([...selectedSourceTypes, sourceType]);
      }
   };

   const getAllPhotos = () => {
      const allPhotos: any[] = [];
      if (selectedSourceTypes.includes('event')) allPhotos.push(...eventPhotos);
      if (selectedSourceTypes.includes('trip')) allPhotos.push(...tripPhotos);
      if (selectedSourceTypes.includes('user_photo')) allPhotos.push(...profilePhotos);
      return allPhotos;
   };

   return (
      <Paper
         sx={{
            p: { xs: 1, sm: 2 },
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
         }}
      >
         <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
         >
            <Typography variant='h6'>Select Photos to Transform</Typography>

            {selectedPhotos.length > 0 && (
               <Chip
                  label={`${selectedPhotos.length} photos selected`}
                  color='primary'
                  onDelete={() => onPhotoSelect([])}
                  deleteIcon={<ClearIcon />}
                  sx={{ ml: 1 }}
               />
            )}
         </Box>

         <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
               placeholder='Search photos...'
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               variant='outlined'
               size='small'
               sx={{ flexGrow: 1, minWidth: '200px' }}
               InputProps={{
                  startAdornment: (
                     <InputAdornment position='start'>
                        <SearchIcon />
                     </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                     <InputAdornment position='end'>
                        <IconButton onClick={() => setSearchTerm('')} edge='end' size='small'>
                           <ClearIcon fontSize='small' />
                        </IconButton>
                     </InputAdornment>
                  ),
               }}
            />

            {/* Source type filter chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
               <Tooltip title='Toggle Event Photos'>
                  <Chip
                     icon={<EventIcon />}
                     label='Events'
                     color={selectedSourceTypes.includes('event') ? 'primary' : 'default'}
                     onClick={() => toggleSourceType('event')}
                     variant={selectedSourceTypes.includes('event') ? 'filled' : 'outlined'}
                  />
               </Tooltip>
               <Tooltip title='Toggle Trip Photos'>
                  <Chip
                     icon={<TripIcon />}
                     label='Trips'
                     color={selectedSourceTypes.includes('trip') ? 'secondary' : 'default'}
                     onClick={() => toggleSourceType('trip')}
                     variant={selectedSourceTypes.includes('trip') ? 'filled' : 'outlined'}
                     sx={{ fontWeight: selectedSourceTypes.includes('trip') ? 'bold' : 'normal' }}
                  />
               </Tooltip>
               <Tooltip title='Toggle Profile Photos'>
                  <Chip
                     icon={<ProfileIcon />}
                     label='Profile'
                     color={selectedSourceTypes.includes('user_photo') ? 'success' : 'default'}
                     onClick={() => toggleSourceType('user_photo')}
                     variant={selectedSourceTypes.includes('user_photo') ? 'filled' : 'outlined'}
                     sx={{
                        fontWeight: selectedSourceTypes.includes('user_photo') ? 'bold' : 'normal',
                     }}
                  />
               </Tooltip>
            </Box>
         </Box>

         <Box sx={{ overflow: 'auto', pb: 1, height: '100%' }}>
            {loading ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
               </Box>
            ) : (
               <Grid container spacing={3}>
                  {renderEnhancedPhotoGrid(getAllPhotos())}
               </Grid>
            )}
         </Box>
      </Paper>
   );
};

export default PhotoSelector;
